import type {
    AddOnFormFieldsFragment,
    AddOnQuoteFieldsFragment,
} from "~graphql/generated/graphql"
import {
    trackAddToCart,
    trackRemoveFromCart,
} from "~utils/event-tracking/event-tracking-helpers"
import type {
    AddOnInput,
    PackageState,
} from "~components/pages/listing/checkout/checkout-flow/checkout-flow-types"

export function onQuantityChange({
    addOn,
    quantity,
    packageState,
    updatePackageState,
}: {
    addOn: AddOnFormFieldsFragment
    quantity: number
    packageState: PackageState
    updatePackageState: (update: Partial<PackageState>) => void
}) {
    if (!quantity) {
        removeAddOnFromPackageState({
            addOn,
            packageState,
            updatePackageState,
        })

        return
    }

    updateAddOnState({
        addOn,
        quantity,
        packageState,
        updatePackageState,
    })
}

function removeAddOnFromPackageState({
    addOn,
    packageState,
    updatePackageState,
}: {
    addOn: AddOnFormFieldsFragment
    packageState: PackageState
    updatePackageState: (update: Partial<PackageState>) => void
}) {
    const existingAddOns = packageState.add_ons || []

    updatePackageState({
        add_ons: existingAddOns.filter(
            (existingAddOn) => existingAddOn.id !== addOn.id
        ),
    })
}

function updateAddOnState({
    addOn,
    quantity,
    packageState,
    updatePackageState,
}: {
    addOn: AddOnFormFieldsFragment
    quantity: number
    packageState: PackageState
    updatePackageState: (update: Partial<PackageState>) => void
}) {
    const existingAddOns = packageState.add_ons || []

    const otherExistingAddOns = existingAddOns.filter(
        (existingAddOn) => existingAddOn.id !== addOn.id
    )
    const formattedAddOn = mapAddOnToAddOnInput({ addOn, quantity })

    trackAddingItemsToCart({ addOn, existingAddOns, quantity })
    updatePackageState({
        add_ons: [...otherExistingAddOns, formattedAddOn],
    })
}

export function mapAddOnToAddOnInput({
    addOn,
    quantity,
}: {
    addOn: AddOnFormFieldsFragment | AddOnQuoteFieldsFragment
    quantity: number
}): AddOnInput {
    return {
        id: addOn.id,
        name: addOn.name,
        category: addOn.category,
        unit_price: addOn.unit_price,
        quantity,
    }
}

function trackAddingItemsToCart({
    addOn,
    existingAddOns,
    quantity,
}: {
    addOn: AddOnFormFieldsFragment
    existingAddOns: AddOnInput[]
    quantity: number
}) {
    const previousAddOn = existingAddOns.find(
        (existingAddOn) => existingAddOn.id === addOn.id
    )
    const previousQuantity = previousAddOn?.quantity || 0

    const { didAddItemsToCart, didRemoveItemsFromCart } = getCartState({
        previousAddOn,
        previousQuantity,
        quantity,
    })

    if (didAddItemsToCart) {
        // We don't wan't to track the quantity that was already in the cart since that would already have been tracked
        const normalizedQuantity = quantity - previousQuantity
        trackAddToCart(addOn, normalizedQuantity)
    }

    if (didRemoveItemsFromCart) {
        // Calculate how many were removed and track that value
        const normalizedQuantity = previousQuantity - quantity
        trackRemoveFromCart(addOn, normalizedQuantity)
    }
}

function getCartState({
    previousAddOn,
    previousQuantity,
    quantity,
}: {
    previousAddOn: AddOnInput | undefined
    previousQuantity: number
    quantity: number
}) {
    // Items have only been added if the new quantity is greater than the old
    // (or the old didn't exist. i.e. it's a new item we're adding)
    const didAddItemsToCart =
        previousAddOn?.quantity === undefined || previousQuantity < quantity

    // Items have only been removed from the cart if the new quantity is less than the old quantity
    const didRemoveItemsFromCart =
        Number.isInteger(previousAddOn?.quantity) && previousQuantity > quantity

    return {
        didAddItemsToCart,
        didRemoveItemsFromCart,
    }
}
